import React, { useEffect, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Box,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { addDays, addMonths, endOfWeek, format, startOfWeek } from "date-fns";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import CalendarDayView from "./CalendarDayView";
import CalendarMonthView from "./CalendarMonthView";
import CalendarWeekView from "./CalendarWeekView";
import { useSchedule } from "../SchedulerProvider";

type CalendarMode = "day" | "week" | "month";

const CalendarView: React.FC = observer(() => {
  const { classes } = useStyles();
  const schedule = useSchedule();
  const [mode, setMode] = useState<CalendarMode>("week");
  const [focusedDate, setFocusedDate] = useState<Date>(new Date());

  // Keyboard undo/redo, ignoring keystrokes aimed at form fields (e.g. the
  // time-picker inputs in the edit popover).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z")
        return;
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      event.preventDefault();
      if (event.shiftKey) schedule.redo();
      else schedule.undo();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [schedule]);

  const shift = (direction: 1 | -1) => {
    setFocusedDate((current) => {
      if (mode === "day") return addDays(current, direction);
      if (mode === "week") return addDays(current, direction * 7);
      return addMonths(current, direction);
    });
  };

  const periodLabel = (() => {
    if (mode === "day") return format(focusedDate, "EEEE, MMM d, yyyy");
    if (mode === "week")
      return `${format(startOfWeek(focusedDate), "MMM d")} – ${format(
        endOfWeek(focusedDate),
        "MMM d, yyyy"
      )}`;
    return format(focusedDate, "MMMM yyyy");
  })();

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <Box className={classes.nav}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFocusedDate(new Date())}
          >
            Today
          </Button>
          <IconButton
            size="small"
            aria-label="previous"
            onClick={() => shift(-1)}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton size="small" aria-label="next" onClick={() => shift(1)}>
            <ChevronRightIcon />
          </IconButton>
          <Typography className={classes.period}>{periodLabel}</Typography>
          <Tooltip title="Undo (⌘Z)">
            <span>
              <IconButton
                size="small"
                aria-label="undo"
                disabled={!schedule.canUndo}
                onClick={() => schedule.undo()}
              >
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (⇧⌘Z)">
            <span>
              <IconButton
                size="small"
                aria-label="redo"
                disabled={!schedule.canRedo}
                onClick={() => schedule.redo()}
              >
                <RedoIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_event, next: CalendarMode | null) => {
            if (next) setMode(next);
          }}
        >
          <ToggleButton value="day" className={classes.toggle}>
            Day
          </ToggleButton>
          <ToggleButton value="week" className={classes.toggle}>
            Week
          </ToggleButton>
          <ToggleButton value="month" className={classes.toggle}>
            Month
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === "day" && <CalendarDayView focusedDate={focusedDate} />}
      {mode === "week" && <CalendarWeekView focusedDate={focusedDate} />}
      {mode === "month" && (
        <CalendarMonthView
          focusedDate={focusedDate}
          onSelectDate={(date) => {
            setFocusedDate(date);
            setMode("day");
          }}
        />
      )}
    </Box>
  );
});

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
    marginTop: 24,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
    gap: 12,
    flexWrap: "wrap",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  period: {
    fontSize: 16,
    fontWeight: 700,
    color: "#363E45",
    marginLeft: 4,
  },
  toggle: {
    textTransform: "none",
    fontWeight: 600,
    paddingLeft: 16,
    paddingRight: 16,
  },
}));

export default CalendarView;
